import type { DataContext, Report, ReportTemplate } from '@/data/schemas';
import { buildKeypath } from '@/data/idGenerator';

export type MissingRequiredField = {
  sectionId: string;
  fieldId: string;
  fieldLabel: string;
  indices?: number[];
};

const EMPTY_STRING = '';

function resolvePath(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, source);
}

function isMissingValue(value: unknown, fieldType: string): boolean {
  if (value === null || value === undefined) return true;

  if (typeof value === 'string') {
    return value.trim() === EMPTY_STRING;
  }

  if (fieldType === 'number') {
    return typeof value !== 'number' || Number.isNaN(value);
  }

  if (fieldType === 'bool') {
    return false;
  }

  return false;
}

export function findMissingRequiredFields(options: {
  report: Report;
  template: ReportTemplate;
  context: DataContext;
}): MissingRequiredField[] {
  const { report, template, context } = options;
  const applicableSections =
    report.meta.applicableSections ?? template.sections.map((section) => section.id);
  const resolvedContext = context as Record<string, unknown>;

  const missing: MissingRequiredField[] = [];

  template.sections.forEach((section) => {
    if (!applicableSections.includes(section.id)) return;
    const requiredFields = section.fields.filter((field) => field.required);
    if (requiredFields.length === 0) return;

    if (section.type === 'repeating' && section.repeat) {
      const repeatValue = resolvePath(resolvedContext, section.repeat.forEach);
      const repeatCount = Array.isArray(repeatValue)
        ? repeatValue.length
        : typeof repeatValue === 'number'
          ? repeatValue
          : 0;
      if (repeatCount <= 0) return;

      for (let index = 0; index < repeatCount; index += 1) {
        requiredFields.forEach((field) => {
          const keypath = buildKeypath(section.id, field.id, [index]);
          const value = report.data.values[keypath];
          if (isMissingValue(value, field.type)) {
            missing.push({
              sectionId: section.id,
              fieldId: field.id,
              fieldLabel: field.label,
              indices: [index],
            });
          }
        });
      }
      return;
    }

    requiredFields.forEach((field) => {
      const keypath = buildKeypath(section.id, field.id);
      const value = report.data.values[keypath];
      if (isMissingValue(value, field.type)) {
        missing.push({
          sectionId: section.id,
          fieldId: field.id,
          fieldLabel: field.label,
        });
      }
    });
  });

  return missing;
}
